import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * Manufacturing Module Service Stores
 * Updated to match backend api-types with comprehensive serviceViews
 */
export const manufacturingStores: ServiceStoreConfig[] = [
  {
    name: "batch-materials",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.batchMaterialsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalMaterials = entities.length;
          const activeToday = entities.filter(
            (material: Record<string, unknown>) => {
              const date = new Date(material.usageDate as string);
              const today = new Date();
              return date.toDateString() === today.toDateString();
            }
          ).length;
          const totalQuantity = entities.reduce(
            (sum: number, material: Record<string, unknown>) => sum + ((material.quantityUsed as number) || 0),
            0
          );
          const uniqueBatches = new Set(
            entities.map((material: Record<string, unknown>) => material.batchId)
          ).size;

          return [
            {
              title: "Total Materials",
              value: () => totalMaterials.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Used Today",
              value: () => activeToday.toString(),
              valueColor: "info.main",
            },
            {
              title: "Total Quantity",
              value: () => totalQuantity.toFixed(0),
              valueColor: "success.main",
            },
            {
              title: "Active Batches",
              value: () => uniqueBatches.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "casting-operations",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.castingOperationsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalOperations = entities.length;
          const inProgress = entities.filter(
            (op: Record<string, unknown>) => op.status === "in_progress"
          ).length;
          const completed = entities.filter(
            (op: Record<string, unknown>) => op.status === "completed"
          ).length;
          const todayOperations = entities.filter(
            (op: Record<string, unknown>) => {
              const date = new Date(op.castingDate as string);
              const today = new Date();
              return date.toDateString() === today.toDateString();
            }
          ).length;

          return [
            {
              title: "Total Operations",
              value: () => totalOperations.toString(),
              valueColor: "primary.main",
            },
            {
              title: "In Progress",
              value: () => inProgress.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Completed",
              value: () => completed.toString(),
              valueColor: "success.main",
            },
            {
              title: "Today",
              value: () => todayOperations.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "curing-records",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.curingRecordsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalRecords = entities.length;
          const curing = entities.filter(
            (record: Record<string, unknown>) => record.curingStatus === "curing"
          ).length;
          const completed = entities.filter(
            (record: Record<string, unknown>) => record.curingStatus === "completed"
          ).length;
          const avgCuringTime = entities.length > 0
            ? entities.reduce((sum: number, record: Record<string, unknown>) => sum + ((record.curingDuration as number) || 0), 0) / entities.length
            : 0;

          return [
            {
              title: "Total Records",
              value: () => totalRecords.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Currently Curing",
              value: () => curing.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Completed",
              value: () => completed.toString(),
              valueColor: "success.main",
            },
            {
              title: "Avg Duration (hrs)",
              value: () => avgCuringTime.toFixed(1),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "embeds-and-inserts",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.embedsAndInsertsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalItems = entities.length;
          const installed = entities.filter(
            (item: Record<string, unknown>) => item.installationStatus === "installed"
          ).length;
          const pending = entities.filter(
            (item: Record<string, unknown>) => item.installationStatus === "pending"
          ).length;
          const uniqueTypes = new Set(
            entities.map((item: Record<string, unknown>) => item.embedType)
          ).size;

          return [
            {
              title: "Total Items",
              value: () => totalItems.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Installed",
              value: () => installed.toString(),
              valueColor: "success.main",
            },
            {
              title: "Pending",
              value: () => pending.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Unique Types",
              value: () => uniqueTypes.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "finishing-operations",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.finishingOperationsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalOperations = entities.length;
          const inProgress = entities.filter(
            (op: Record<string, unknown>) => op.status === "in_progress"
          ).length;
          const completed = entities.filter(
            (op: Record<string, unknown>) => op.status === "completed"
          ).length;
          const qcPassed = entities.filter(
            (op: Record<string, unknown>) => op.qualityCheckStatus === "passed"
          ).length;

          return [
            {
              title: "Total Operations",
              value: () => totalOperations.toString(),
              valueColor: "primary.main",
            },
            {
              title: "In Progress",
              value: () => inProgress.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Completed",
              value: () => completed.toString(),
              valueColor: "success.main",
            },
            {
              title: "QC Passed",
              value: () => qcPassed.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "mix-designs",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.mixDesignsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalDesigns = entities.length;
          const active = entities.filter(
            (design: Record<string, unknown>) => design.isActive === true
          ).length;
          const approved = entities.filter(
            (design: Record<string, unknown>) => design.approvalStatus === "approved"
          ).length;
          const avgStrength = entities.length > 0
            ? entities.reduce((sum: number, design: Record<string, unknown>) => sum + ((design.targetStrength as number) || 0), 0) / entities.length
            : 0;

          return [
            {
              title: "Total Designs",
              value: () => totalDesigns.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => active.toString(),
              valueColor: "success.main",
            },
            {
              title: "Approved",
              value: () => approved.toString(),
              valueColor: "info.main",
            },
            {
              title: "Avg Strength (psi)",
              value: () => avgStrength.toFixed(0),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "mix-design-admixtures",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.mixDesignAdmixturesStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalAdmixtures = entities.length;
          const uniqueTypes = new Set(
            entities.map((admixture: Record<string, unknown>) => admixture.admixtureType)
          ).size;
          const totalDosage = entities.reduce(
            (sum: number, admixture: Record<string, unknown>) => sum + ((admixture.dosageRate as number) || 0),
            0
          );
          const uniqueDesigns = new Set(
            entities.map((admixture: Record<string, unknown>) => admixture.mixDesignId)
          ).size;

          return [
            {
              title: "Total Admixtures",
              value: () => totalAdmixtures.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Unique Types",
              value: () => uniqueTypes.toString(),
              valueColor: "info.main",
            },
            {
              title: "Total Dosage",
              value: () => totalDosage.toFixed(2),
              valueColor: "warning.main",
            },
            {
              title: "Mix Designs",
              value: () => uniqueDesigns.toString(),
              valueColor: "success.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "manhour-capacity",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        summaryCards: (rootStore) => {
          const store = rootStore.manhourCapacityStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalStandards = entities.length;
          const latestStandard = entities.length > 0
            ? entities.reduce((latest: Record<string, unknown>, current: Record<string, unknown>) => {
                const latestDate = new Date(latest.effectiveDate as string);
                const currentDate = new Date(current.effectiveDate as string);
                return currentDate > latestDate ? current : latest;
              }, entities[0] as Record<string, unknown>)
            : null;
          const currentCapacity = latestStandard
            ? (latestStandard.totalDailyCapacity as number) || 0
            : 0;
          const currentWorkers = latestStandard
            ? (latestStandard.workerCount as number) || 0
            : 0;

          return [
            {
              title: "Capacity Standards",
              value: () => totalStandards.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Current Capacity",
              value: () => `${currentCapacity.toFixed(0)} hrs`,
              valueColor: "success.main",
            },
            {
              title: "Current Workers",
              value: () => currentWorkers.toString(),
              valueColor: "info.main",
            },
            {
              title: "Latest Effective",
              value: () => latestStandard
                ? new Date(latestStandard.effectiveDate as string).toLocaleDateString()
                : "N/A",
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "molds",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.moldsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalMolds = entities.length;
          const available = entities.filter(
            (mold: Record<string, unknown>) => mold.status === "available"
          ).length;
          const inUse = entities.filter(
            (mold: Record<string, unknown>) => mold.status === "in_use"
          ).length;
          const maintenance = entities.filter(
            (mold: Record<string, unknown>) => mold.status === "maintenance"
          ).length;

          return [
            {
              title: "Total Molds",
              value: () => totalMolds.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Available",
              value: () => available.toString(),
              valueColor: "success.main",
            },
            {
              title: "In Use",
              value: () => inUse.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Maintenance",
              value: () => maintenance.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "mold-usage-log",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.moldUsageLogStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalUsages = entities.length;
          const todayUsages = entities.filter(
            (usage: Record<string, unknown>) => {
              const date = new Date(usage.usageDate as string);
              const today = new Date();
              return date.toDateString() === today.toDateString();
            }
          ).length;
          const uniqueMolds = new Set(
            entities.map((usage: Record<string, unknown>) => usage.moldId)
          ).size;
          const avgCycleTime = entities.length > 0
            ? entities.reduce((sum: number, usage: Record<string, unknown>) => sum + ((usage.cycleTime as number) || 0), 0) / entities.length
            : 0;

          return [
            {
              title: "Total Usages",
              value: () => totalUsages.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Today",
              value: () => todayUsages.toString(),
              valueColor: "info.main",
            },
            {
              title: "Active Molds",
              value: () => uniqueMolds.toString(),
              valueColor: "success.main",
            },
            {
              title: "Avg Cycle (min)",
              value: () => avgCycleTime.toFixed(1),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "production-batches",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.productionBatchesStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalBatches = entities.length;
          const inProgress = entities.filter(
            (batch: Record<string, unknown>) => batch.status === "in_progress"
          ).length;
          const completed = entities.filter(
            (batch: Record<string, unknown>) => batch.status === "completed"
          ).length;
          const totalQuantity = entities.reduce(
            (sum: number, batch: Record<string, unknown>) => sum + ((batch.quantityProduced as number) || 0),
            0
          );

          return [
            {
              title: "Total Batches",
              value: () => totalBatches.toString(),
              valueColor: "primary.main",
            },
            {
              title: "In Progress",
              value: () => inProgress.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Completed",
              value: () => completed.toString(),
              valueColor: "success.main",
            },
            {
              title: "Total Produced",
              value: () => totalQuantity.toFixed(0),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "production-equipment",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.productionEquipmentStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalEquipment = entities.length;
          const operational = entities.filter(
            (equipment: Record<string, unknown>) => equipment.status === "operational"
          ).length;
          const maintenance = entities.filter(
            (equipment: Record<string, unknown>) => equipment.status === "maintenance"
          ).length;
          const downtime = entities.filter(
            (equipment: Record<string, unknown>) => equipment.status === "down"
          ).length;

          return [
            {
              title: "Total Equipment",
              value: () => totalEquipment.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Operational",
              value: () => operational.toString(),
              valueColor: "success.main",
            },
            {
              title: "Maintenance",
              value: () => maintenance.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Down",
              value: () => downtime.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "production-schedule",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.productionScheduleStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalScheduled = entities.length;
          const today = entities.filter(
            (schedule: Record<string, unknown>) => {
              const date = new Date(schedule.scheduledDate as string);
              const now = new Date();
              return date.toDateString() === now.toDateString();
            }
          ).length;
          const completed = entities.filter(
            (schedule: Record<string, unknown>) => schedule.status === "completed"
          ).length;
          const delayed = entities.filter(
            (schedule: Record<string, unknown>) => schedule.status === "delayed"
          ).length;

          return [
            {
              title: "Total Scheduled",
              value: () => totalScheduled.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Today",
              value: () => today.toString(),
              valueColor: "info.main",
            },
            {
              title: "Completed",
              value: () => completed.toString(),
              valueColor: "success.main",
            },
            {
              title: "Delayed",
              value: () => delayed.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "production-summary",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
    },
  },
  {
    name: "production-tickets",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.productionTicketsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalTickets = entities.length;
          const open = entities.filter(
            (ticket: Record<string, unknown>) => ticket.status === "open"
          ).length;
          const inProgress = entities.filter(
            (ticket: Record<string, unknown>) => ticket.status === "in_progress"
          ).length;
          const completed = entities.filter(
            (ticket: Record<string, unknown>) => ticket.status === "completed"
          ).length;

          return [
            {
              title: "Total Tickets",
              value: () => totalTickets.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Open",
              value: () => open.toString(),
              valueColor: "warning.main",
            },
            {
              title: "In Progress",
              value: () => inProgress.toString(),
              valueColor: "info.main",
            },
            {
              title: "Completed",
              value: () => completed.toString(),
              valueColor: "success.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "reinforcement-schedule",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.reinforcementScheduleStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalItems = entities.length;
          const installed = entities.filter(
            (item: Record<string, unknown>) => item.installationStatus === "installed"
          ).length;
          const pending = entities.filter(
            (item: Record<string, unknown>) => item.installationStatus === "pending"
          ).length;
          const totalWeight = entities.reduce(
            (sum: number, item: Record<string, unknown>) => sum + ((item.weight as number) || 0),
            0
          );

          return [
            {
              title: "Total Items",
              value: () => totalItems.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Installed",
              value: () => installed.toString(),
              valueColor: "success.main",
            },
            {
              title: "Pending",
              value: () => pending.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Total Weight (lbs)",
              value: () => totalWeight.toFixed(0),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "stripping-records",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.strippingRecordsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalRecords = entities.length;
          const todayRecords = entities.filter(
            (record: Record<string, unknown>) => {
              const date = new Date(record.strippingDate as string);
              const today = new Date();
              return date.toDateString() === today.toDateString();
            }
          ).length;
          const passed = entities.filter(
            (record: Record<string, unknown>) => record.qualityCheck === "passed"
          ).length;
          const failed = entities.filter(
            (record: Record<string, unknown>) => record.qualityCheck === "failed"
          ).length;

          return [
            {
              title: "Total Records",
              value: () => totalRecords.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Today",
              value: () => todayRecords.toString(),
              valueColor: "info.main",
            },
            {
              title: "QC Passed",
              value: () => passed.toString(),
              valueColor: "success.main",
            },
            {
              title: "QC Failed",
              value: () => failed.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
];
